import React from 'react';

import * as ArchiveActions from 'archive/ArchiveActions';
import BenchlingConfig from 'BenchlingConfig';
import * as BenchlingUtil from 'BenchlingUtil';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {isRegistrySupportedType} from 'constants/RegistryConstants';
import {TagSchemaSupportedFolderItemType} from 'graphql-client/__generated/graphqlGlobalTypes';
import navigateToURL from 'lib/navigateToURL';
import {trackOpenedRelevantTemplatesTab} from 'nav/analytics/RelevantTemplatesAnalytics';
import {Organization} from 'orgs/OrganizationConstants';
import * as RegistryUtil from 'registry/RegistryUtil';
import {RELEVANT_TEMPLATES_TAB_NAME} from 'schemas/relevant-templates/RelevantTemplatesConstants';
import RelevantTemplatesTab from 'schemas/relevant-templates/RelevantTemplatesTab';
import SchemaSettingsPageContainer, {ArchiveSchemaConfig} from 'schemas/SchemaSettingsPageContainer';
import useTagSchemaQuery from 'schemas/useTagSchemaQuery';
import getRegistrySchemasTitle from 'settings/registry/getRegistrySchemasTitle';
import TagSchemaFileSettings from 'settings/registry/TagSchemaFileSettings';
import TagSchemaPermissionSettings from 'settings/registry/TagSchemaPermissionSettings';
import TagSchemaValidationManagement from 'settings/registry/TagSchemaValidationManagement';
import TagSchemaValidationSettingsPage from 'settings/registry/TagSchemaValidationSettings';
import EntityStore from 'stores/EntityStore';
import * as TagSchemaActions from 'tag-schema/TagSchemaActions';
import getTestProps from 'test/lib/cypress/getTestProps';
import {Tab, TabContainer} from 'ui-library/TabContainer';
import licenses from 'user/Licenses';

import {TagSchemaGraphQL} from '../../../graphql-fragments/schemas/tag-schemas/__generated/TagSchemaGraphQL';

export enum TagSchemaSettingsTab {
  FILE = 'FILE',
  VALIDATION = 'VALIDATION',
  VALIDATION_MANAGEMENT = 'VALIDATION_MANAGEMENT',
  RELEVANT_TEMPLATES = 'RELEVANT_TEMPLATES',
  ACCESS_POLICIES = 'ACCESS POLICIES',
}

function routeSectionForSettingsTab(tab: TagSchemaSettingsTab): string {
  const settingsTabToRouteSection = {
    [TagSchemaSettingsTab.FILE]: '',
    [TagSchemaSettingsTab.VALIDATION]: '/validation',
    [TagSchemaSettingsTab.VALIDATION_MANAGEMENT]: '/validation-management',
    [TagSchemaSettingsTab.RELEVANT_TEMPLATES]: '/relevant-templates',
    [TagSchemaSettingsTab.ACCESS_POLICIES]: '/access-policies',
  };
  return settingsTabToRouteSection[tab];
}

export function settingsTabForRouteSection(routeSection: string): TagSchemaSettingsTab | null {
  const routeSectionToTab: {[routeSection: string]: TagSchemaSettingsTab} = {
    'entity-settings': TagSchemaSettingsTab.FILE,
    validation: TagSchemaSettingsTab.VALIDATION,
    'validation-management': TagSchemaSettingsTab.VALIDATION_MANAGEMENT,
    'relevant-templates': TagSchemaSettingsTab.RELEVANT_TEMPLATES,
    'access-policies': TagSchemaSettingsTab.ACCESS_POLICIES,
  };
  return routeSectionToTab[routeSection];
}

interface QueryingTagSchemaSettingsPageProps {
  ownerUrlId: string;
  registryId: string;
  tagSchemaId: string;
  activeTab?: TagSchemaSettingsTab;
}
interface TagSchemaSettingsPageProps extends QueryingTagSchemaSettingsPageProps {
  tagSchema: TagSchemaGraphQL | null;
  archiveConfig?: ArchiveSchemaConfig;
}
interface State {
  activeTab: TagSchemaSettingsTab;
}

export class TagSchemaSettingsPage extends React.Component<TagSchemaSettingsPageProps, State> {
  override state: State = {
    activeTab: TagSchemaSettingsTab.FILE,
  };

  private trackOpenTabEvent(tab: TagSchemaSettingsTab): void {
    if (tab === TagSchemaSettingsTab.RELEVANT_TEMPLATES) {
      trackOpenedRelevantTemplatesTab({
        schemaId: this.props.tagSchemaId,
        schemaType: ModelTypeConstants.TAG_SCHEMA,
        isAdmin: EntityStore.currentUser().organizations.some((org: Organization) => org.admin),
      });
    }
  }

  override componentDidMount(): void {
    if (this.props.activeTab) {
      this.handleSetActiveTab(this.props.activeTab);
      this.trackOpenTabEvent(this.props.activeTab);
    }
  }

  override componentDidUpdate(prevProps: TagSchemaSettingsPageProps, prevState: State): void {
    if (prevProps.activeTab !== this.props.activeTab) {
      this.handleSetActiveTab(this.props.activeTab || TagSchemaSettingsTab.FILE);
      this.trackOpenTabEvent(this.props.activeTab || TagSchemaSettingsTab.FILE);
    }
  }

  private navigateToUrlForSettingsTab(tab: TagSchemaSettingsTab): void {
    const routeSegmentForTab = routeSectionForSettingsTab(tab);
    const tagSchemaSettingsUrl = RegistryUtil.getTagSchemaSettingsUrl(
      this.props.ownerUrlId,
      this.props.registryId,
      this.props.tagSchemaId
    );
    navigateToURL(`${tagSchemaSettingsUrl}${routeSegmentForTab}`);
  }

  private handleSetActiveTab = (tab: TagSchemaSettingsTab): void => {
    this.setState({activeTab: tab});
    this.navigateToUrlForSettingsTab(tab);
  };

  private shouldRenderRelevantTemplatesTab(): boolean {
    return (
      this.props.tagSchema != null &&
      this.props.tagSchema.folderItemType !== TagSchemaSupportedFolderItemType.entry
    );
  }

  private shouldRenderValidationSettingsTab(): boolean {
    return (
      !BenchlingConfig.VALIDATION_RULES_ENGINE_ENABLE_TAG_SCHEMAS &&
      BenchlingConfig.REGISTRY_ENABLE_ASYNC_VALIDATION_SETTINGS_TAB &&
      this.props.tagSchema != null &&
      isRegistrySupportedType(this.props.tagSchema.folderItemType)
    );
  }

  private shouldRenderValidationManagementTab(): boolean {
    return (
      BenchlingConfig.VALIDATION_RULES_ENGINE_ENABLE_TAG_SCHEMAS &&
      this.props.tagSchema != null &&
      isRegistrySupportedType(this.props.tagSchema.folderItemType)
    );
  }

  private shouldRenderPermissionSettingsTab(): boolean {
    return this.props.tagSchemaId != null;
  }

  private handleClickSchemas = (): void => {
    BenchlingUtil.navigate(
      null,
      RegistryUtil.getRegistrySchemasSettingsUrl(this.props.ownerUrlId, this.props.registryId)
    );
  };

  private handleExportSpreadsheetTemplate = (): void => {
    TagSchemaActions.exportSpreadsheetTemplate(this.props.tagSchemaId);
  };

  private getSanitizedActiveTab(): TagSchemaSettingsTab {
    if (
      (this.state.activeTab === TagSchemaSettingsTab.VALIDATION &&
        !this.shouldRenderValidationSettingsTab()) ||
      (this.state.activeTab === TagSchemaSettingsTab.VALIDATION_MANAGEMENT &&
        !this.shouldRenderValidationManagementTab()) ||
      (this.state.activeTab === TagSchemaSettingsTab.ACCESS_POLICIES &&
        !this.shouldRenderPermissionSettingsTab()) ||
      (this.state.activeTab === TagSchemaSettingsTab.RELEVANT_TEMPLATES &&
        !this.shouldRenderRelevantTemplatesTab())
    ) {
      return TagSchemaSettingsTab.FILE;
    }
    return this.state.activeTab;
  }

  override render(): JSX.Element {
    if (this.props.tagSchema) {
      BenchlingUtil.setWindowTitle(`${this.props.tagSchema.name} - ${getRegistrySchemasTitle()}`);
    }

    let schema = null;
    if (
      [
        TagSchemaSettingsTab.FILE,
        TagSchemaSettingsTab.VALIDATION,
        TagSchemaSettingsTab.VALIDATION_MANAGEMENT,
        TagSchemaSettingsTab.RELEVANT_TEMPLATES,
        TagSchemaSettingsTab.ACCESS_POLICIES,
      ].includes(this.state.activeTab)
    ) {
      schema = this.props.tagSchema;
    }

    return (
      <SchemaSettingsPageContainer
        archiveConfig={this.props.archiveConfig}
        onExportSpreadsheetTemplate={this.handleExportSpreadsheetTemplate}
        onNavigateToListing={this.handleClickSchemas}
        schema={schema}
        schemaTypeName="Entity Schema"
        showExport={licenses.hasEnterprisePlan()}
      >
        <div className="u-flex-grow" {...getTestProps({component: 'RegistrySettingsPage', element: 'root'})}>
          <TabContainer
            activeTab={this.getSanitizedActiveTab()}
            className="u-flex-grow"
            onRequestTabChange={this.handleSetActiveTab}
          >
            <Tab tabKey={TagSchemaSettingsTab.FILE} title="Entity Settings">
              <TagSchemaFileSettings
                key={this.props.tagSchemaId}
                ownerUrlId={this.props.ownerUrlId}
                registryId={this.props.registryId}
                tagSchemaId={this.props.tagSchemaId}
              />
            </Tab>
            {this.shouldRenderPermissionSettingsTab() && (
              <Tab tabKey={TagSchemaSettingsTab.ACCESS_POLICIES} title="Access Policies">
                <TagSchemaPermissionSettings tagSchemaId={this.props.tagSchemaId} />
              </Tab>
            )}
            {this.shouldRenderRelevantTemplatesTab() && (
              <Tab tabKey={TagSchemaSettingsTab.RELEVANT_TEMPLATES} title={RELEVANT_TEMPLATES_TAB_NAME}>
                <RelevantTemplatesTab
                  schemaId={this.props.tagSchemaId}
                  schemaOrgUrlId={this.props.ownerUrlId}
                />
              </Tab>
            )}
            {this.shouldRenderValidationSettingsTab() && (
              <Tab tabKey={TagSchemaSettingsTab.VALIDATION} title="Validation Failures">
                <TagSchemaValidationSettingsPage tagSchemaId={this.props.tagSchemaId} />
              </Tab>
            )}
            {this.shouldRenderValidationManagementTab() && (
              <Tab tabKey={TagSchemaSettingsTab.VALIDATION_MANAGEMENT} title="Validation">
                <TagSchemaValidationManagement tagSchemaId={this.props.tagSchemaId} />
              </Tab>
            )}
          </TabContainer>
        </div>
      </SchemaSettingsPageContainer>
    );
  }
}

export default function QueryingTagSchemaSettingsPage(
  props: QueryingTagSchemaSettingsPageProps
): JSX.Element {
  const {tagSchema, refetch} = useTagSchemaQuery(props.tagSchemaId);

  const archiveConfig: ArchiveSchemaConfig | undefined = tagSchema
    ? {
        onArchive: async (id: string, purpose: string): Promise<void> => {
          await ArchiveActions.bulkArchiveTagSchemas([id], purpose);
          refetch();
        },
        onUnarchive: async (id: string): Promise<void> => {
          await ArchiveActions.bulkUnarchiveTagSchemas([id]);
          refetch();
        },
        isArchivable: Boolean(tagSchema.permissions.archivable),
        isUnarchivable: Boolean(tagSchema.permissions.unarchivable),
      }
    : undefined;

  return <TagSchemaSettingsPage {...props} tagSchema={tagSchema} archiveConfig={archiveConfig} />;
}
